import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../../../../lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../../../components/ui/alert-dialog";
import { buttonVariants } from "../../../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { ActionLink, ActionsCell, EmptyState, SearchBox, SectionCard, StatusPill, useAccountingPermissions } from "./shared";
import { bankAccountBlank, bankAccountTypeLabels } from "./constants";
import { ACCOUNTING_BASE } from "./accountingNav";
import BankAccountForm from "./BankAccountForm";

function maskLocally(value) {
  const digits = String(value || "");
  if (!digits) return "-";
  if (digits.length <= 4) return "*".repeat(digits.length);
  return `${"*".repeat(digits.length - 4)}${digits.slice(-4)}`;
}

export default function BankAccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [bankLedgers, setBankLedgers] = useState([]);
  const [billingSettings, setBillingSettings] = useState(null);
  const [balances, setBalances] = useState({});
  const [form, setForm] = useState(bankAccountBlank);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const perms = useAccountingPermissions();
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    try {
      const [accountsRes, ledgersRes, settingsRes] = await Promise.all([
        // includeInactive=true so a deactivated account stays visible (with a Reactivate action) instead
        // of silently disappearing - it must remain a manageable record, not become a dead end.
        api.get("/society/accounting/engine/bank-accounts?includeInactive=true").catch(() => ({ data: [] })),
        api.get("/society/accounting/engine/ledgers").catch(() => ({ data: [] })),
        api.get("/society/billing/settings").catch(() => ({ data: null })),
      ]);
      const rows = Array.isArray(accountsRes.data) ? accountsRes.data : [];
      setAccounts(rows);
      setBankLedgers((Array.isArray(ledgersRes.data) ? ledgersRes.data : []).filter((l) => l.ledgerGroup === "bank"));
      setBillingSettings(settingsRes.data || null);
      const balanceEntries = await Promise.all(rows.map(async (row) => {
        try {
          const res = await api.get(`/society/accounting/engine/bank-accounts/${row.id}/balance`);
          return [row.id, res.data];
        } catch {
          return [row.id, null];
        }
      }));
      setBalances(Object.fromEntries(balanceEntries));
    } catch (err) {
      toast.error(formatErr(err, "Could not load bank accounts"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Matches the backend's own uniqueness check, which only blocks reuse of a ledger that's linked to an
  // ACTIVE bank account - a deactivated account's ledger becomes available again for a new one.
  const linkedLedgerIds = useMemo(() => new Set(accounts.filter((a) => a.active !== false).map((a) => a.linkedLedgerId)), [accounts]);
  const availableLedgers = useMemo(() => bankLedgers.filter((l) => !linkedLedgerIds.has(l.id)), [bankLedgers, linkedLedgerIds]);

  const filteredAccounts = useMemo(() => accounts.filter((a) => {
    if (statusFilter === "active" && a.active === false) return false;
    if (statusFilter === "inactive" && a.active !== false) return false;
    const q = search.trim().toLowerCase();
    if (q && !String(a.bankName || "").toLowerCase().includes(q) && !String(a.accountName || "").toLowerCase().includes(q)) return false;
    return true;
  }), [accounts, search, statusFilter]);

  const toggleActive = async (account) => {
    try {
      // linkedLedgerId is intentionally omitted - the normal update endpoint never changes it.
      await api.patch(`/society/accounting/engine/bank-accounts/${account.id}`, {
        bankName: account.bankName, branch: account.branch, accountName: account.accountName,
        ifsc: account.ifsc, accountType: account.accountType,
        isPrimaryCollectionAccount: account.isPrimaryCollectionAccount, reconciliationEnabled: account.reconciliationEnabled,
        active: !(account.active !== false), isLinkedToBillingSettlement: account.isLinkedToBillingSettlement,
      });
      toast.success(account.active === false ? "Bank account reactivated" : "Bank account deactivated");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update bank account status"));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await api.delete(`/society/accounting/engine/bank-accounts/${deleteTarget.id}`);
      toast.success("Bank account deleted");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      // The backend re-checks on every delete attempt (never trusts the canDelete flag shown here), so a
      // 409 here means accounting records were linked to this account after the list last loaded.
      toast.error(formatErr(err, "This bank account cannot be deleted because accounting records are linked to it. Deactivate it instead."));
    } finally {
      setDeleting(false);
    }
  };

  const createAccount = async () => {
    if (!form.bankName.trim()) {
      toast.error("Bank name is required");
      return;
    }
    try {
      setBusy(true);
      // Normal flow: no linkedLedgerId is sent at all - the backend creates a brand-new,
      // dedicated ledger named `ledgerName` for this physical account. Advanced flow
      // (form.linkedLedgerId set via the "Advanced: Link existing accounting ledger"
      // toggle): send it and drop ledgerName, since the backend won't create a ledger.
      const payload = { ...form, openingBalance: form.openingBalance === "" ? undefined : Number(form.openingBalance) };
      if (payload.linkedLedgerId) {
        delete payload.ledgerName;
      } else {
        delete payload.linkedLedgerId;
      }
      await api.post("/society/accounting/engine/bank-accounts", payload);
      toast.success("Bank account created");
      setForm(bankAccountBlank);
      setShowAddForm(false);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not save bank account"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-extrabold text-[#111827]">Bank Accounts</h2>
          <p className="mt-1 text-sm text-[#6B7280]">
            The Accounting Bank Account master used by vouchers, Bank Book and Bank Reconciliation. Balances are always derived from
            opening balance plus posted vouchers, never entered directly.
          </p>
        </div>
        {!showAddForm && perms.canCreate && (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="rounded-sm bg-[#111827] px-4 py-2 text-sm font-semibold text-white hover:bg-[#374151]"
          >
            + Add bank account
          </button>
        )}
      </div>

      {showAddForm && (
        <SectionCard title="Add bank account" subtitle="Account numbers are masked everywhere except an explicit, audited reveal.">
          <BankAccountForm
            form={form}
            setForm={setForm}
            availableLedgers={availableLedgers}
            allowAdvancedLink
            busy={busy}
            onSubmit={createAccount}
            onCancel={() => { setShowAddForm(false); setForm(bankAccountBlank); }}
            submitLabel="Add bank account"
            isEdit={false}
          />
        </SectionCard>
      )}

      <SectionCard title="Bank account master">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <SearchBox value={search} onChange={setSearch} placeholder="Search bank or account name..." />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : accounts.length === 0 ? (
          <div className="text-sm text-[#6B7280]">No accounting bank accounts yet. Add one above and link it to a Bank-group ledger.</div>
        ) : filteredAccounts.length === 0 ? (
          <EmptyState>No bank accounts match this filter.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7EB] text-left text-xs uppercase tracking-[0.12em] text-[#6B7280]">
                  <th className="py-2 pr-3">Bank</th>
                  <th className="py-2 pr-3">Account name</th>
                  <th className="py-2 pr-3">Account no.</th>
                  <th className="py-2 pr-3">Linked ledger</th>
                  <th className="py-2 pr-3">Primary</th>
                  <th className="py-2 pr-3">Reconciliation</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3 text-right">Balance</th>
                  <th className="py-2 pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map((account) => (
                  <tr key={account.id} className="border-b border-[#F3F4F6]">
                    <td className="py-2 pr-3 font-semibold text-[#111827]">
                      {account.bankName}
                      <div className="text-xs font-normal text-[#6B7280]">{bankAccountTypeLabels[account.accountType] || account.accountType}{account.branch ? ` · ${account.branch}` : ""}</div>
                    </td>
                    <td className="py-2 pr-3">{account.accountName || "-"}</td>
                    <td className="py-2 pr-3 font-mono">{maskLocally(account.accountNumber)}</td>
                    <td className="py-2 pr-3">{account.linkedLedgerName || "-"}</td>
                    <td className="py-2 pr-3">{account.isPrimaryCollectionAccount ? <span className="rounded-sm bg-[#111827] px-2 py-0.5 text-xs font-semibold text-white">Primary</span> : "-"}</td>
                    <td className="py-2 pr-3">{account.reconciliationEnabled ? "Enabled" : "Disabled"}</td>
                    <td className="py-2 pr-3"><StatusPill active={account.active !== false} /></td>
                    <td className="py-2 pr-3 text-right font-semibold">₹ {Number(balances[account.id]?.balance || 0).toLocaleString()}</td>
                    <td className="py-2 pr-3">
                      <ActionsCell>
                        <ActionLink to={`${ACCOUNTING_BASE}/bank-accounts/${account.id}`}>View</ActionLink>
                        <span className="text-[#D1D5DB]">|</span>
                        <ActionLink to={`${ACCOUNTING_BASE}/reports/bank-book?bankAccountId=${account.id}`}>View Bank Book</ActionLink>
                        {perms.canCreate && (
                          <>
                            <span className="text-[#D1D5DB]">|</span>
                            <ActionLink to={`${ACCOUNTING_BASE}/bank-accounts/${account.id}/edit`}>Edit</ActionLink>
                            <span className="text-[#D1D5DB]">|</span>
                            <ActionLink tone={account.active === false ? "default" : "danger"} onClick={() => toggleActive(account)}>
                              {account.active === false ? "Reactivate" : "Deactivate"}
                            </ActionLink>
                          </>
                        )}
                        {account.canDelete && perms.canManageMasters && (
                          <>
                            <span className="text-[#D1D5DB]">|</span>
                            <ActionLink tone="danger" onClick={() => setDeleteTarget(account)}>Delete</ActionLink>
                          </>
                        )}
                      </ActionsCell>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Billing collection settlement account" subtitle="A separate, existing operational setting used only for receiving/settling Billing collections. It is not the accounting source of truth and is not edited here.">
        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : billingSettings ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
            <div><div className="text-xs text-[#6B7280]">Bank</div><div className="font-semibold text-[#111827]">{billingSettings.bankName || "-"}</div></div>
            <div><div className="text-xs text-[#6B7280]">Account name</div><div className="font-semibold text-[#111827]">{billingSettings.bankAccountName || "-"}</div></div>
            <div><div className="text-xs text-[#6B7280]">Account number</div><div className="font-mono font-semibold text-[#111827]">{maskLocally(billingSettings.bankAccountNumber)}</div></div>
            <div><div className="text-xs text-[#6B7280]">IFSC / UPI</div><div className="font-mono font-semibold text-[#111827]">{billingSettings.ifsc || billingSettings.upiId || "-"}</div></div>
          </div>
        ) : (
          <div className="text-sm text-[#6B7280]">Not set up yet.</div>
        )}
        <p className="mt-3 text-xs text-[#6B7280]">
          Edit these details from Society Panel &rarr; Billing &rarr; Auto Bill Settings. If this is the same physical account as one of the
          Accounting Bank Accounts above, mark that account "Is primary collection account" - the two records stay independent.
        </p>
      </SectionCard>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete bank account?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-[#374151]">
                <div className="font-mono font-semibold text-[#111827]">
                  {deleteTarget?.bankName} {maskLocally(deleteTarget?.accountNumber)}
                </div>
                <p>This bank account has no accounting transactions and can be permanently deleted.</p>
                <p className="font-semibold text-red-600">This action cannot be undone.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={deleting} onClick={confirmDelete} className={buttonVariants({ variant: "destructive" })}>
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
